import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  Accordion,
  Select,
  Typography,
  Input,
  Autocomplete,
  OptionProps,
  QuotaSelect,
  Search,
  QuotaOptionProps,
  Info,
  DSInputType,
  SlimButton,
  LoadingModal
} from 'components'
import { getClientOvTypes } from 'services/client/ovTypes'
import * as S from './styles'
import { useDispatch, useSelector } from 'store/store'
import { Controller, useFormContext } from 'react-hook-form'
import { TypesValidations } from 'constants/enums'
import { getClientAddress, getSaleRegions } from 'services/salesOrder'
import { Cliente } from 'container/CreateOrder'
import { getClientCredit } from 'services/client/credit'
import { useCallbackPrompt } from 'commons/customHooks/useCallbackPrompt'
import { getCustomers } from 'services/client/list'
import { QuotaMonth } from 'container/SalesOrder/interfaces'
import { PaymentMethodsProps } from 'container/CreateOrder/components/TopSection'
import { isValidNumber } from 'commons/utils/string'
import { ClientCrediInfo } from 'services/client/credit/interfaces'
import { useMatch, useParams } from 'react-router-dom'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ActionPerm } from '../Header/constants'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { numberToDecimalString } from 'commons/utils/number'
import { getLastDayOfMonth } from 'commons/utils/date'
import {
  MaterialType,
  OVOrder,
  OVTypes,
  channelOptions,
  materialTypeOptions,
  sectorOptions,
  unfoldOptions
} from 'constants/enums'
import { setIsYTRA } from 'store/OVContext/ovSlice'
import { PAYMENT_TERM_CASH_CODE } from 'constants/paymentTerms'

const MAX_LIST_SIZE = 10

type OVHeaderProps<T extends OVOrder> = {
  type: T
  postDraft: () => Promise<void>
  quotasMonth: QuotaMonth[]
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_escr_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
  >
  paymentMethods: PaymentMethodsProps[]
  addressOptions: OptionProps[]
  /** @deprecated List will comming from API */
  clientOvTypes?: OptionProps[]
  inputAutocomplete: string
  inputAutocompleteSaleRegion: string
  setAddressOptions: Dispatch<SetStateAction<OptionProps[]>>
  setInputAutocomplete: Dispatch<SetStateAction<string>>
  defaultExpanded: boolean
  loading?: boolean
  disabled?: boolean
  disableClientConfig?: never
  disableFields?: never
  disableOrderConfig?: never
  subHeader?: never
  setClientOvTypes?: never
  setInputAutocompleteSaleRegion?: never
  setSubHeader?: never
  hasOvs?: never
}

type OrderHeaderProps<T extends OVOrder> = {
  type: T
  subHeader: ClientCrediInfo
  setSubHeader: Dispatch<SetStateAction<ClientCrediInfo>>
  clientOvTypes: OptionProps[]
  setClientOvTypes: Dispatch<SetStateAction<OptionProps[]>>
  addressOptions: OptionProps[]
  setAddressOptions: Dispatch<SetStateAction<OptionProps[]>>
  inputAutocomplete: string
  setInputAutocomplete: Dispatch<SetStateAction<string>>
  inputAutocompleteSaleRegion: string
  setInputAutocompleteSaleRegion: Dispatch<SetStateAction<string>>
  disabled?: boolean
  disableClientConfig?: boolean
  disableFields?: boolean
  disableOrderConfig?: boolean
  quotasMonth: QuotaMonth[]
  postDraft: () => Promise<void>
  paymentMethods: PaymentMethodsProps[]
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_escr_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
  >
  loading?: boolean
  defaultExpanded?: boolean
  hasOvs?: boolean
}

export type OVOrderHeaderProps<T extends OVOrder> = T extends OVOrder.ORDER
  ? OrderHeaderProps<T>
  : OVHeaderProps<T>

export const OVOrderHeader = <T extends OVOrder>({
  disableClientConfig,
  disabled = false,
  disableFields,
  disableOrderConfig,
  subHeader,
  setClientOvTypes,
  setInputAutocompleteSaleRegion,
  setSubHeader,
  defaultClient,
  quotasMonth,
  paymentMethods,
  type,
  clientOvTypes,
  addressOptions,
  inputAutocomplete,
  inputAutocompleteSaleRegion,
  setAddressOptions,
  setInputAutocomplete,
  defaultExpanded,
  postDraft,
  loading,
  hasOvs
}: OVOrderHeaderProps<T>) => {
  const dispatch = useDispatch()
  const { control, setValue, getValues, formState, trigger, watch, reset } =
    useFormContext()
  const { isDirty } = formState
  useCallbackPrompt(isDirty)
  const { ovNumber } = useParams()
  const { ovSelected, isYTRA } = useSelector(({ ov }) => ov)
  const { records } = useSelector((state) => state.salesOrder)
  const { orderStep } = useSelector((store) => store.structure)
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action)
  }

  const isOrder = type === OVOrder.ORDER

  let codigoCanalDistribuicao = ovSelected?.Cabecalho?.CodigoCanalDistribuicao
  const codigoTipoOrdem = ovSelected?.Cabecalho?.CodigoTipoOrdem ?? ''

  const dataToHeader = {
    cd_tipo_orde: codigoTipoOrdem,
    cd_canl_dstb: ovSelected?.Cabecalho?.CodigoCanalDistribuicao ?? '',
    nr_dias_adic: ovSelected?.Cabecalho?.PrazoExtra?.toString() ?? '0',
    cd_clie_entr:
      addressOptions?.find(
        (address) => address.title === ovSelected?.Cabecalho?.EnderecoEntrega
      )?.value ?? '',
    nr_pedi_clie: ovSelected?.Cabecalho?.NumeroPedidoCliente ?? ' ',
    dt_ano_mes_cota: ovSelected?.Cabecalho?.MesCota ?? '',
    ds_grup_clie: ovSelected?.Cabecalho?.CanalDistribuicao ?? '',
    pc_dsct: ovSelected?.Cabecalho?.Desconto ?? 0,
    cd_seto_atvd: ovSelected?.Cabecalho?.Setor?.substring(0, 2) ?? '',
    nr_insc_estl: ovSelected?.Cabecalho?.InscricaoEstadual ?? '',
    fl_mto_prep:
      ovSelected?.Cabecalho?.TipoMaterial === MaterialType.MTO
        ? TypesValidations.YES
        : TypesValidations.NO,
    cd_cent_matr: ovSelected?.Cabecalho?.CodigoCentroMaterial ?? '',
    fl_prmt_desd: TypesValidations.YES,
    cd_cond_pgto: ovSelected?.Cabecalho?.CondicaoPagamento?.substring(0, 4) ?? ''
  }
  const defaultValues = { ...getValues(), ...dataToHeader }

  codigoCanalDistribuicao = codigoCanalDistribuicao ?? channelOptions[0].value

  const dt_ano_mes_cota = watch('dt_ano_mes_cota')
  const selectedOvType = watch('cd_tipo_orde')
  const regiaoVenda = watch('cd_regi_vend')
  const channelDist = watch('cd_canl_dstb')

  const isYEXR = useMemo(() => selectedOvType === OVTypes.YEXR, [selectedOvType])

  useEffect(() => {
    dispatch(setIsYTRA(selectedOvType === OVTypes.YTRA))

    if (selectedOvType === OVTypes.YTRA) {
      setValue('cd_cond_pgto', PAYMENT_TERM_CASH_CODE)
    }
  }, [selectedOvType])

  const quotaMonthOptions: QuotaOptionProps[] =
    selectedOvType === OVTypes.YRBA && orderStep === 0
      ? quotasMonth
          .filter((month) => month.fl_ano_mes_cota_antp !== TypesValidations.YES)
          .map((month) => ({
            title: month.ds_ano_mes_cota,
            value: month.dt_ano_mes_cota
          }))
      : quotasMonth.map((month) => ({
          title: month.ds_ano_mes_cota,
          value: month.dt_ano_mes_cota,
          anticipated: month.fl_ano_mes_cota_antp === TypesValidations.YES
        }))

  const [loadingClient, setLoadingClient] = useState(false)
  const [clearNumber, setClearNumber] = useState<number>(0)
  const [searchPage, setSearchPage] = useState<number>(0)
  const [searchWidth, setSearchWidth] = useState(0)
  const [shouldSaveQuota, setShouldSaveQuota] = useState(false)
  const [baseKey, setBaseKey] = useState(0)
  const [saleRegions, setSaleRegions] = useState<OptionProps[]>([])
  const [selectedClient, setSelectedClient] = useState({
    cd_clie: '',
    nm_clie: '',
    nr_cnpj_cpf: ''
  })

  const saleRegionsDivRef = useRef<HTMLDivElement>(null)
  const clientDivRef = useRef<HTMLDivElement>(null)
  const sapDivRef = useRef<HTMLDivElement>(null)
  const cnpjDivRef = useRef<HTMLDivElement>(null)
  const searchClientRef = useRef<HTMLDivElement>(null)
  const match = useMatch('/gerenciar/pedido/:orderNumber')
  useLayoutEffect(() => {
    setSearchWidth(searchClientRef?.current?.offsetWidth || 0)
  }, [searchClientRef?.current?.offsetWidth])

  const disableOnOV = (bool?: boolean, ovDis?: boolean) => {
    // NOTE: Follow the priority of importance
    if (disabled) {
      return true
    }

    if (match) {
      return hasOvs || bool
    }

    if (ovNumber) {
      if (ovSelected?.Cabecalho?.CodigoStatus == 0) {
        if (ovDis === undefined) {
          return true
        }
        return ovDis
      }

      return true
    } else {
      return bool
    }
  }

  const disableQuotaMonth = (defaultCondition: boolean) => {
    // NOTE: Follow the priority of importance
    if (disabled) {
      return true
    }

    if (match) {
      return (
        records.length > 0 &&
        !records.some((record) => [0, 20].includes(record.cd_stat_pedi_rasc_item))
      )
    }

    if (ovNumber) {
      if (+ovSelected?.Cabecalho?.CodigoStatus !== 0) {
        return true
      }
    }
    return defaultCondition
  }

  const returnToDefault = () => {
    reset(defaultValues)
    const condition = paymentMethods?.find(
      (pay) => pay.cd_cond_pgto === defaultValues.cd_cond_pgto
    )

    setInputAutocomplete(
      condition?.ds_cond_pgto
        ? `${condition.cd_cond_pgto} - ${condition.ds_cond_pgto}`
        : ''
    )
  }

  const handleNumberInput = (currentValue: string, value: string): string => {
    if (value === '') return '0'
    if (!isValidNumber(value)) return currentValue
    else return `${Number(value)}`
  }

  useEffect(() => {
    if (dt_ano_mes_cota !== '' && shouldSaveQuota) {
      postDraft()
    }
  }, [dt_ano_mes_cota])

  useEffect(() => {
    let isMounted = true

    const fetchSalesRegions = async () => {
      const regionsData = await getSaleRegions(channelDist)
      if (isMounted) {
        let regionsOptions: OptionProps[] = []

        if (regionsData) {
          regionsOptions = regionsData.data
            .filter((regions) => !!regions.cd_regi_vend)
            .map((region) => ({
              value: region.cd_regi_vend,
              title: region.ds_regi_vend
            }))
        }

        setSaleRegions(regionsOptions)
      }
    }

    setSaleRegions([])
    fetchSalesRegions()

    return () => {
      isMounted = false
    }
  }, [channelDist])

  useEffect(() => {
    if (saleRegions && saleRegions.length === 1) {
      setValue('cd_regi_vend', saleRegions[0].value)
    }
  }, [saleRegions])

  const getListSAPAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_clie: inputValue,
      qt_rowcount: MAX_LIST_SIZE,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_pagi: page,
      cd_regi_vend: regiaoVenda,
      cd_canl_dstb: channelDist
    })

    return returnApi
  }

  const getListAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      nm_pcro: inputValue,
      qt_rowcount: MAX_LIST_SIZE,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_pagi: page,
      cd_regi_vend: regiaoVenda,
      cd_canl_dstb: channelDist
    })

    return returnApi
  }

  const getListCNPJAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      nr_cnpj_cpf_pesq: inputValue,
      qt_rowcount: MAX_LIST_SIZE,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_pagi: page,
      cd_regi_vend: regiaoVenda,
      cd_canl_dstb: channelDist
    })

    return returnApi
  }

  const getDefaultInput = (selectedValue: 'cd_clie' | 'nr_cnpj_cpf' | 'nm_clie') => {
    if (
      selectedClient &&
      selectedClient[selectedValue] &&
      selectedClient[selectedValue] !== ''
    ) {
      return selectedClient[selectedValue]
    }
    if (defaultClient && defaultClient[selectedValue] !== '') {
      return defaultClient[selectedValue]
    }

    return ''
  }

  const handleQuotaChange = (quota: string) => {
    setValue('dt_dsjd_pedi', getLastDayOfMonth(quota, 'YYYY-MM-DD'))

    if (quotaMonthOptions.find((option) => option.value === quota)?.anticipated) {
      setValue('fl_matr_fora_linh', TypesValidations.NO)
    }

    setShouldSaveQuota(true)
  }

  const handleOvTypeChange = (ovType: string) => {
    if (ovType === OVTypes.YRBA) {
      const currentMonth = getValues('dt_ano_mes_cota')
      const validMonths = quotaMonthOptions.filter((month) => !month.anticipated)
      const isQuotaValid = validMonths.find((item) => item.value === currentMonth)

      if (!isQuotaValid) {
        const firstOption = quotaMonthOptions[0]?.value as string

        handleQuotaChange(firstOption)
        setValue('dt_ano_mes_cota', firstOption)
      }
    }
  }

  const handleClientSelect = async (e: Cliente) => {
    setLoadingClient(true)
    setValue('cd_clie', e)
    setValue('cd_cond_pgto', '')
    setValue('address', '')
    setValue('nr_cnpj_cpf', e.nr_cnpj_cpf)

    const { cd_empr, cd_orgn_vend, cd_canl_dstb, cd_seto_atvd, cd_clie, cd_escr_vend } = e

    await getClientAddress({
      cd_empr,
      cd_orgn_vend,
      cd_canl_dstb,
      cd_seto_atvd,
      cd_clie
    }).then((response) => {
      if (setAddressOptions)
        setAddressOptions(
          response?.map((add) => ({
            value: add.cd_pcro,
            title: add.ds_endr
          }))
        )
      if (response.length === 1) {
        setValue('address', response[0].cd_pcro)
      }
    })

    // TODO: Check if necessary. Probably duplicated or bad location
    await getClientOvTypes(cd_clie, cd_escr_vend).then((types) => {
      const ovTypes = types?.map((type: string) => {
        return { title: type, value: type }
      })

      if (!types.includes(selectedOvType)) {
        setValue('cd_tipo_orde', types[0])
      }

      if (setClientOvTypes) setClientOvTypes(ovTypes)
    })

    await getClientCredit(cd_clie).then((credit) => {
      if (setSubHeader) setSubHeader(credit)
    })

    setLoadingClient(false)
    postDraft()
  }

  const toDecimal = (value: string) => numberToDecimalString(+value)

  const clearRegion = () => {
    setValue('cd_regi_vend', '')
    setValue('ds_regi_vend', '')
    if (setInputAutocompleteSaleRegion) setInputAutocompleteSaleRegion('')
  }

  const clearClient = () => {
    setValue('cd_clie', {})
    setValue('nr_cnpj_cpf', '---')
    setValue('nr_insc_estl', '--')
    setValue('address', '')
    if (setAddressOptions) setAddressOptions([])
    setClearNumber(1)
    setTimeout(() => setClearNumber(0), 500)
  }

  const renderSearch = (
    key: string,
    label: string,
    id: string,
    defaultInputValue: 'cd_clie' | 'nm_clie' | 'nr_cnpj_cpf',
    placeHolder: string,
    loadOptions: ((inputValue: string, page: string) => Promise<Cliente[]>) | undefined,
    infoText: React.ReactNode,
    isRightSided?: boolean
  ) => (
    <Search<Cliente>
      key={`${key}-${baseKey}`}
      label={label}
      id={id}
      listWidth={`${searchWidth}px`}
      labelsConcat={[defaultInputValue]}
      defaultInputValue={getDefaultInput(`${defaultInputValue}`)}
      placeHolder={placeHolder}
      loadOptions={loadOptions}
      type="select"
      minimumInput={0}
      isClientSearch
      optionsMaxHeight="400px"
      optionsLimit={MAX_LIST_SIZE}
      keysColumnsOption={['nm_clie']}
      columnsSizeOption={['100%']}
      loadOnFocus
      valueCase="upper"
      flexWrapOptionDescription
      setPage={setSearchPage}
      page={searchPage}
      clearInput={clearNumber}
      setOptionSelected={(e) => {
        setSelectedClient({
          cd_clie: e.cd_clie,
          nm_clie: e.nm_clie,
          nr_cnpj_cpf: e.nr_cnpj_cpf
        })
        setBaseKey(baseKey + 1)
        handleClientSelect(e)
      }}
      disabled={disableOnOV(disableOrderConfig || !regiaoVenda)}
      isRightSided={isRightSided}
      hideIcon
      labelAdornment={<Info iconSize={12}>{infoText}</Info>}
    />
  )

  return (
    <S.ConfigClient type={type}>
      {loadingClient && <LoadingModal />}

      <Accordion
        defaultExpanded={match ? false : defaultExpanded}
        summaryContent={
          <S.TitleRow>
            <span id="MaterialsInsertTitle">Cabeçalho</span>
          </S.TitleRow>
        }
      >
        <S.OrderConfig>
          <S.FieldWrapper templateColumns="1fr 1fr" mediaTemplateColumns="1fr">
            <S.FieldWrapper templateColumns="1fr 1fr" mediaTemplateColumns="1.4fr 1fr">
              <S.FieldWrapper templateColumns=".1fr .7fr .4fr">
                <div className="fieldItemWrapper">
                  <Controller
                    name="cd_canl_dstb"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Mercado"
                        disabled={disableOnOV(disableClientConfig || disableFields)}
                        value={value}
                        defaultValue={codigoCanalDistribuicao}
                        id="cd_canl_dstb"
                        name={name}
                        focused={false}
                        options={channelOptions}
                        onChange={(e) => {
                          onChange(e)
                          clearRegion()
                          clearClient()
                        }}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    name="cd_seto_atvd"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        label="Setor"
                        disabled={disableOnOV(disableClientConfig || disableFields)}
                        value={value}
                        defaultValue={sectorOptions[0].value}
                        id="cd_seto_atvd"
                        name={name}
                        focused={false}
                        options={sectorOptions}
                        onChange={(e) => {
                          onChange(e)
                          clearClient()
                        }}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    name="fl_mto_prep"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        disabled={disableOnOV(disableClientConfig)}
                        value={value}
                        defaultValue={TypesValidations.NO}
                        id="tipo-material"
                        label="Tipo de pedido"
                        name={name}
                        focused={false}
                        options={materialTypeOptions}
                        onChange={(e) => {
                          onChange(e)
                          if (e.target.value === TypesValidations.YES) {
                            setValue('ds_text_item_mto', '')
                          } else {
                            setValue('ds_text_item_mto', undefined)
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
              <div className="fieldItemWrapper regions" ref={saleRegionsDivRef}>
                <Controller
                  name="cd_regi_vend"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { name, ref, onChange }, fieldState: { error } }) => (
                    <Autocomplete<OptionProps>
                      placeHolder="Clique para selecionar"
                      disabled={disableOnOV(!saleRegions?.length || disableFields)}
                      label="Região de venda"
                      ref={ref}
                      data={saleRegions}
                      browserAutocomplete="off"
                      inputId={name}
                      objId={'value'}
                      objDescription={'title'}
                      error={error?.message}
                      input={inputAutocompleteSaleRegion}
                      setInput={
                        setInputAutocompleteSaleRegion ||
                        (() => {
                          return
                        })
                      }
                      onInputChange={() => {
                        setValue('cd_regi_vend', '', { shouldDirty: true })
                        setValue('ds_regi_vend', '')
                      }}
                      setOptionSelected={(e) => {
                        if (e?.value) {
                          onChange(e.value)
                          setValue('cd_regi_vend', e.title)
                          trigger(['cd_regi_vend', 'ds_regi_vend'])
                        }
                      }}
                    />
                  )}
                />
              </div>
            </S.FieldWrapper>
            <S.FieldWrapper
              templateColumns=".15fr .25fr .8fr"
              mediaTemplateColumns=".3fr .5fr 1.6fr"
              ref={searchClientRef}
            >
              <div className="fieldItemWrapper" ref={sapDivRef} id="cod_sap">
                {renderSearch(
                  'sap-key',
                  'Código SAP',
                  'cod_sap',
                  'cd_clie',
                  'ˍ ˍ ˍ ˍ ˍ ˍ ˍ',
                  getListSAPAsync,
                  <>
                    Ao preencher este campo, os campos “CPF/CNPJ” e “Razão social” serão
                    automaticamente preenchidos. <br /> <br />
                    As configurações dos campos “Setor” e “Região de venda” podem afetar
                    os resultados disponíveis neste campo.
                  </>
                )}
              </div>
              <div className="fieldItemWrapper" ref={cnpjDivRef} id="cnpj">
                {renderSearch(
                  'cnpj-key',
                  'CPF/CNPJ',
                  'cnpj',
                  'nr_cnpj_cpf',
                  'ˍ ˍ.ˍ ˍ ˍ.ˍ ˍ ˍ ˍ/ˍ ˍ ˍ ˍ-ˍˍ',
                  getListCNPJAsync,
                  <>
                    Ao preencher este campo, os campos “Código SAP” e “Razão social” serão
                    automaticamente preenchidos. <br /> <br /> As configurações dos campos
                    “Setor” e “Região de venda” podem afetar os resultados disponíveis
                    neste campo.
                  </>,
                  true
                )}
              </div>
              <div className="fieldItemWrapper" ref={clientDivRef} id="client_name">
                {renderSearch(
                  'client-key',
                  'Razão Social',
                  'client',
                  'nm_clie',
                  'Selecione uma região de venda para buscar um cliente',
                  getListAsync,
                  <>
                    Ao preencher este campo, os campos “Código SAP” e “CPF/CNPJ” serão
                    automaticamente preenchidos. <br /> <br />
                    As configurações dos campos “Setor” e “Região de venda” podem afetar
                    os resultados disponíveis neste campo.
                  </>,
                  true
                )}
              </div>
            </S.FieldWrapper>
          </S.FieldWrapper>
          <S.FieldWrapper templateColumns="1fr 1fr" mediaTemplateColumns="1fr">
            <S.FieldWrapper templateColumns="1fr 1fr" mediaTemplateColumns="1.4fr 1fr">
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="outerFieldItemWrapper">
                  <div className="fieldItemWrapper">
                    <Controller
                      name="dt_ano_mes_cota"
                      control={control}
                      render={({ field: { name, value, onChange } }) => (
                        <QuotaSelect
                          name={name}
                          value={value}
                          label="Mês cota"
                          options={quotaMonthOptions}
                          labelAdornment={
                            <Info
                              iconSize={12}
                            >{`Para preencher este campo é necessário preencher os campos de cliente.`}</Info>
                          }
                          disabled={disableQuotaMonth(
                            disableOrderConfig || !quotaMonthOptions.length
                          )}
                          onChange={(e) => {
                            const valueQuotaMonth = e.target?.value
                            if (typeof valueQuotaMonth === 'string') {
                              handleQuotaChange(valueQuotaMonth)
                            }
                            onChange(e)
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    name="fl_prmt_desd"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        value={value}
                        id="desdobrar"
                        label="Desdobro"
                        name={name}
                        focused={false}
                        options={unfoldOptions}
                        onChange={onChange}
                        disabled={disableOnOV(disableOrderConfig)}
                        labelAdornment={
                          <Info iconSize={12}>
                            {`Ao selecionar a opção "Fábrica", cada fábrica vai gerar a sua
                        Ordem de Venda (OV); Ao selecionar "Material", cada material
                        vai gerar a sua Ordem de Venda (OV).`}
                          </Info>
                        }
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="fieldItemWrapper">
                  <S.LabelInfos>Subcanal</S.LabelInfos>
                  <S.DivStyled>
                    <Typography id="subcanal" design="hav-default-03-text">
                      {getValues('ds_grup_clie')}
                    </Typography>
                  </S.DivStyled>
                </div>

                <div className="fieldItemWrapper">
                  <S.LabelInfos>Insc. estadual</S.LabelInfos>
                  <S.DivStyled>
                    <Typography id="nr_insc_estl" design="hav-default-03-text">
                      {getValues('nr_insc_estl')}
                    </Typography>
                  </S.DivStyled>
                </div>
              </S.FieldWrapper>
            </S.FieldWrapper>
            <div className="fieldItemWrapper">
              {addressOptions.length === 1 ? (
                <>
                  <S.LabelInfos>Endereço de entrega</S.LabelInfos>
                  <S.DivStyled>
                    <Typography id="end-entrega-solo" design="hav-default-03-text">
                      {addressOptions[0].title}
                    </Typography>
                  </S.DivStyled>
                </>
              ) : (
                <Controller
                  name="cd_clie_entr"
                  control={control}
                  defaultValue=""
                  render={({ field: { value, onChange, name } }) => (
                    <Select
                      name={name}
                      id="end-entrega"
                      menuText="Selecione um cliente para buscar endereços"
                      value={value}
                      label="Endereço de entrega"
                      options={addressOptions}
                      onChange={(e) => {
                        onChange(e)
                        if (isOrder) postDraft()
                      }}
                      disabled={
                        !addressOptions.length ||
                        disableOnOV(
                          disableOrderConfig,
                          shouldDisable(ACTIONS_SCREENS.SAVE)
                        )
                      }
                    />
                  )}
                />
              )}
            </div>
          </S.FieldWrapper>
          <S.FieldWrapper templateColumns="1fr 1fr" mediaTemplateColumns="1fr">
            <S.FieldWrapper templateColumns="1fr 1fr">
              <S.FieldWrapper templateColumns={isOrder ? '1fr' : '.7fr .4fr'}>
                <div className="fieldItemWrapper">
                  <Controller
                    render={({
                      field: { onChange, ref, name },
                      fieldState: { error }
                    }) => (
                      <Autocomplete<PaymentMethodsProps>
                        useCodeOnDescription
                        ref={ref}
                        placeHolder="Clique para selecionar"
                        label="Condições de pagamento"
                        data={paymentMethods}
                        browserAutocomplete="off"
                        disabled={
                          disableOnOV(
                            disableOrderConfig,
                            shouldDisable(ACTIONS_SCREENS.SAVE)
                          ) ||
                          isYTRA ||
                          !clientOvTypes?.length
                        }
                        inputId={name}
                        objId={'cd_cond_pgto'}
                        objDescription={'ds_cond_pgto'}
                        error={error?.message}
                        input={inputAutocomplete}
                        setInput={setInputAutocomplete}
                        onInputChange={() => {
                          setValue('cd_cond_pgto', '', { shouldDirty: true })
                          setValue('ds_cond_pgto', '')
                        }}
                        setOptionSelected={(e) => {
                          if (e?.cd_cond_pgto) {
                            onChange(e.cd_cond_pgto)
                            setValue('ds_cond_pgto', e.ds_cond_pgto)
                            trigger(['cd_cond_pgto', 'ds_cond_pgto'])
                          }
                        }}
                      />
                    )}
                    name="cd_cond_pgto"
                    control={control}
                    rules={{ required: true }}
                  />
                </div>
                {type === 'OV' && (
                  <div className="fieldItemWrapper">
                    <Controller
                      name="cd_cent_matr"
                      control={control}
                      render={({
                        field: { name, value, onChange },
                        fieldState: { error }
                      }) => (
                        <Input
                          label="Fábrica"
                          name={name}
                          value={value}
                          maxLength={2}
                          error={error?.message}
                          disabled={disableOnOV(disableOrderConfig)}
                          onChange={onChange}
                          InputProps={{
                            placeholder: 'Ex: 0001',
                            fullWidth: true
                          }}
                        />
                      )}
                    />
                  </div>
                )}
              </S.FieldWrapper>
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="fieldItemWrapper">
                  <Controller
                    name="cd_tipo_orde"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        name={name}
                        label="Tipo de OV"
                        id="tipo-ov"
                        options={clientOvTypes}
                        value={value}
                        disabled={
                          disableOnOV(disableOrderConfig || disableClientConfig) ||
                          isYTRA ||
                          !clientOvTypes?.length
                        }
                        onChange={(e) => {
                          handleOvTypeChange(e.target.value as string)
                          onChange(e)
                        }}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    name="nr_dias_adic"
                    control={control}
                    render={({
                      field: { name, value, onChange },
                      fieldState: { error }
                    }) => (
                      <Input
                        label="Dias extras"
                        name={name}
                        value={value}
                        maxLength={2}
                        error={error?.message}
                        disabled={
                          isYTRA ||
                          isYEXR ||
                          disableOnOV(
                            disableOrderConfig,
                            shouldDisable(ACTIONS_SCREENS.SAVE)
                          )
                        }
                        onChange={(e) => {
                          const newValue = handleNumberInput(value, e.target.value)
                          onChange(newValue)
                        }}
                        InputProps={{
                          placeholder: 'Ex: 60',
                          fullWidth: true,
                          endAdornment: 'dias'
                        }}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
            </S.FieldWrapper>
            <div className="fieldItemWrapper">
              <Controller
                name="nr_pedi_clie"
                control={control}
                defaultValue=""
                render={({ field: { name, value, onChange } }) => (
                  <Input
                    name={name}
                    id="pedi-clie"
                    label="N° de pedido do cliente"
                    design={DSInputType.SEARCH_INPUT}
                    value={value as string}
                    type="text"
                    maxLength={20}
                    showMaxLength
                    maxLenghtPosition="top"
                    disabled={disableOnOV(
                      disableOrderConfig,
                      shouldDisable(ACTIONS_SCREENS.SAVE)
                    )}
                    labelAdornment={'(opcional)'}
                    onChange={(e) => {
                      onChange(e)
                    }}
                    InputProps={{
                      fullWidth: true
                    }}
                  />
                )}
              />
            </div>
          </S.FieldWrapper>
          {getValues('fl_mto_prep') === TypesValidations.YES && isOrder && (
            <>
              <S.FieldWrapper templateColumns="1fr" mediaTemplateColumns="1fr">
                <div>
                  <Controller
                    name="ds_text_item_mto"
                    control={control}
                    defaultValue={''}
                    render={({
                      field: { name, value, onChange, ref },
                      fieldState: { error }
                    }) => (
                      <Input
                        id="MTODescription"
                        name={name}
                        value={value}
                        ref={ref}
                        label="Informação para expedição"
                        rows={1}
                        disabled={disableOnOV(disableOrderConfig)}
                        type="text"
                        error={error?.message}
                        showMaxLength
                        maxLength={255}
                        maxLenghtPosition="top"
                        InputProps={{
                          placeholder: 'Digite aqui a informação necessária',
                          inputProps: { maxLength: 255 }
                        }}
                        labelAdornment={
                          <S.LabelWrapper>
                            <span>(Exibido por pedido MTO)</span>
                            <Info iconSize={12}>
                              Ao selecionar o tipo de pedido como “MTO” (Make To Order),
                              se torna necessário preencher este campo detalhando as
                              especificidades exigidas na preparação do produto, como os
                              dados exibidos nas caixas dos materiais ou dados de
                              etiqueta.
                            </Info>
                          </S.LabelWrapper>
                        }
                        onChange={onChange}
                        required
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
            </>
          )}
          {type === 'OV' && isDirty && (
            <>
              <S.FieldWrapper templateColumns="1fr" mediaTemplateColumns="1fr">
                <S.FormActions>
                  <SlimButton
                    content="Salvar"
                    type="button"
                    variant="primary"
                    loading={loading}
                    disabled={loading || disabled}
                    onClick={() => postDraft()}
                  />
                  <SlimButton
                    content="Desfazer"
                    type="button"
                    variant="secondary"
                    disabled={loading || disabled}
                    onClick={() => returnToDefault()}
                  />
                </S.FormActions>
              </S.FieldWrapper>
            </>
          )}

          {subHeader && Object.keys(subHeader).length > 0 && isOrder && (
            <>
              <S.Divider />
              <S.CreditRow>
                <S.CreditItem>
                  <S.LabelInfos>Limite de crédito</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_limt_cred)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Compromissado</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_comp)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Saldo disponível</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_sald_disp)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Comp. especial</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_cprm_espl)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Total de vendas</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_vend)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Total a receber</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_arec)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Utilização</S.LabelInfos>
                  <Typography>{`${toDecimal(subHeader.pc_grau_utlz)} %`}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Classe de risco</S.LabelInfos>
                  <Typography>{subHeader.ds_clae_risc}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Venc. limite de créd</S.LabelInfos>
                  <Typography>{subHeader.dt_prox_verf}</Typography>
                </S.CreditItem>
              </S.CreditRow>
            </>
          )}
        </S.OrderConfig>
      </Accordion>
    </S.ConfigClient>
  )
}
